import React, { useState, useEffect, useRef } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Cookies } from "react-cookie";
import { toast } from "react-toastify";
import DatePicker from "../../components/AppDatePicker";
import closureApi from "../../api/closureApi";
import RetreatApi from "../../api/retreatApi";
import CustomDataTable from "../../components/CustomDataTable";
import moment from "moment-timezone";
import AlertModal from "../../components/AlertModel";
import ConfirmationAuditModal from "../../components/ConfirmationAuditModal";
import MailRecipientsModal from "./Mailrecipientsmodal";
import { useTrainingGuide } from "../../components/AdminTrainingGuide";
import { getTrainingGuide, trainingMediaSrc } from "../../data/trainingGuides";
import "../../assets/css/RetreatClosure.css";

const SUPER_ADMIN_CONFIRM_EMAILS = [
  "jimmieflores@gmail.com",
  "manpreet02701@gmail.com",
  "kolkebeckd@gmail.com",
];

const DEFAULT_CLOSE_FILTER = "open";
const CLOSE_FILTER_TITLES = {
  closed: "Retreats Validated & Closed",
  open: "Retreats Not Closed",
  inactive: "Inactive Retreats",
};

const isFlagOn = (status) =>
  status === true || status === 1 || status === "1" || status === "true";

const isRetreatClosed = (status, confirmedByAdmin) =>
  isFlagOn(status) || isFlagOn(confirmedByAdmin);

const parseJsonField = (value, fallback) => {
  if (value == null || value === "") return fallback;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

const formatRetreatTitle = (row) =>
  [
    row?.parishName,
    row?.language
      ? `(${row.language[0].toUpperCase()}${row.language.slice(1)})`
      : null,
    row?.location
      ? `${row.location[0].toUpperCase()}${row.location.slice(1)}`
      : null,
  ]
    .filter(Boolean)
    .join(" ");

const shortenTitle = (value, maxWords = 70) => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return "Untitled retreat";
  const words = text.split(" ");
  if (words.length <= maxWords) return text;
  return `${words.slice(0, maxWords).join(" ")}...`;
};

const formatRetreatDates = (from, to) => {
  const start = moment(from).tz("America/New_York");
  const end = moment(to).tz("America/New_York");
  if (start.isSame(end, "day")) return start.format("MMM D, YYYY");
  if (start.isSame(end, "month")) {
    return `${start.format("MMM D")} – ${end.format("D, YYYY")}`;
  }
  return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
};

const getClosureChecks = (row) => {
  const summary = row?.validationSummary || {};
  const closed = isRetreatClosed(
    row?.retreatCloseStatus,
    row?.confirmedByAdmin,
  );
  return [
    {
      key: "roster",
      label: "Roster",
      valid: Boolean(summary.rosterData?.valid),
      message:
        summary.rosterData?.message || "Roster details are not available yet.",
    },
    {
      key: "directory",
      label: "Directory",
      valid: Boolean(summary.directory?.valid),
      message:
        summary.directory?.message || "Directory has not been confirmed.",
    },
    {
      key: "program",
      label: "Program",
      valid: Boolean(summary.program?.valid),
      message: summary.program?.message || "Program has not been confirmed.",
    },
    {
      key: "pictures",
      label: "Pictures",
      valid: Boolean(summary.pictures?.valid),
      message:
        summary.pictures?.message || "Picture check is not available yet.",
    },
    {
      key: "closed",
      label: "Closed",
      valid: closed,
      message: closed
        ? "This retreat is validated and closed."
        : "This retreat is still open. Close it from the roster after the other confirmations are complete.",
    },
  ];
};

const ClosureGuideCard = ({ guide, onOpen }) => {
  if (!guide) return null;
  return (
    <button
      type="button"
      className="rc-guide-card"
      onClick={onOpen}
      aria-label={`Watch ${guide.title}`}
    >
      <span className="rc-guide-media">
        <img src={trainingMediaSrc(guide.thumbnail)} alt="" />
        <span className="rc-guide-shine" aria-hidden="true" />
        <span className="rc-guide-play" aria-hidden="true">
          <i className="mdi mdi-play" />
        </span>
        <span className="rc-guide-time">{guide.lengthLabel}</span>
      </span>
      <span className="rc-guide-name">{guide.title}</span>
    </button>
  );
};

const StatusPill = ({ label, valid, message }) => (
  <OverlayTrigger
    placement="top"
    delay={{ show: 180, hide: 80 }}
    overlay={
      <Tooltip className="rc-tip">
        <div className="rc-tip-body">
          <strong>{valid ? "Complete" : "Needs attention"}</strong>
          <span>{message}</span>
        </div>
      </Tooltip>
    }
  >
    <button type="button" className={`rc-pill ${valid ? "is-ok" : "is-open"}`}>
      <i
        className={`mdi ${valid ? "mdi-check" : "mdi-alert-circle-outline"}`}
        aria-hidden="true"
      />
      {label}
    </button>
  </OverlayTrigger>
);

const ClosuresList = () => {
  const training = useTrainingGuide();
  const rosterGuide = getTrainingGuide("what-is-roster");
  const directoryGuide = getTrainingGuide("what-is-directory");
  const picturesGuide = getTrainingGuide("what-is-pictures");
  const ClosureApi = new closureApi();
  const retreatApi = new RetreatApi();
  const loggedInUser = new Cookies().get("LoveStrongUser") || null;
  const canConfirmAsSuperAdmin = SUPER_ADMIN_CONFIRM_EMAILS.includes(
    loggedInUser?.email,
  );
  const requestId = useRef(0);
  const searchTimer = useRef(null);
  const closeFilterRef = useRef(DEFAULT_CLOSE_FILTER);
  const dateStateRef = useRef({
    filterType: "all",
    dateFrom: null,
    dateTo: null,
  });

  const [allRetreatClosures, setAllRetreatClosures] = useState([]);
  const [searchKey, setSearchKey] = useState("");
  const [LoadData, setLoadData] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [selectedRetreats, setSelectedRetreats] = useState([]);
  const [userData, setUserData] = useState(null);
  const [showingRetreats, setShowingRetreats] = useState(false);
  const [locationData, setLocationData] = useState({
    state: { parishValue: null },
  });
  const [retreatDetail, setRetreatDetail] = useState([]);
  const [confirmedData, setConfirmedData] = useState(false);
  const [leadMail, setLeadMail] = useState(false);
  const [mailMessage, setMailMessage] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [closeFilter, setCloseFilter] = useState(DEFAULT_CLOSE_FILTER);
  const [summary, setSummary] = useState({
    ended: 0,
    closed: 0,
    open: 0,
    inactive: 0,
  });
  const [superAdminConfirmRow, setSuperAdminConfirmRow] = useState(null);
  const showSuperAdminConfirm =
    canConfirmAsSuperAdmin && closeFilter !== "closed";

  closeFilterRef.current = closeFilter;
  dateStateRef.current = {
    filterType: dateFilter,
    dateFrom: dateFrom ? moment(dateFrom).format("YYYY-MM-DD") : null,
    dateTo: dateTo ? moment(dateTo).format("YYYY-MM-DD") : null,
  };

  const CLOSURE_CONTACT_EMAIL = "info@lovestrongmarriage.com";

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const joinLabels = (labels) => {
  if (labels.length <= 1) return labels[0] || "";
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
};

const plainRetreatTitle = (row) =>
  formatRetreatTitle(row)
    .replace(/<[^>]*>/g, " ")
    .replace(/https?:\/\/\S+/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const buildMailMessage = (row) => {
  const checks = getClosureChecks(row);
  const doneCount = checks.filter((item) => item.valid).length;
  const totalCount = checks.length;
  const percent = Math.round((doneCount / totalCount) * 100);
  const pending = checks.filter((item) => !item.valid);
  const retreatName = escapeHtml(plainRetreatTitle(row) || "this Retreat");
  const completedOn = row?.dateTo
    ? moment(row.dateTo).tz("America/New_York").format("D MMMM YYYY")
    : "";
  const pendingMailName = (item) =>
    item.key === "closed"
      ? "clicking the “Retreat Validated and Closed” button"
      : item.label;
  const pendingNames = joinLabels(pending.map(pendingMailName));
  const remainingLabel =
    pending.length === 0
      ? "0 Pending"
      : `${pending.length} Pending (${pending.map(pendingMailName).join(", ")})`;
  let pendingSentence = "Everything is confirmed and this Retreat is closed.";
  if (pending.length === 1 && pending[0].key === "closed") {
    pendingSentence = `The remaining pending item is ${pendingNames}.`;
  } else if (pending.length === 1) {
    pendingSentence = `You are just missing ${pendingNames}. Kindly complete this item, validate the data, and proceed with closing the Retreat.`;
  } else if (pending.length > 1) {
    pendingSentence = `The remaining pending items are ${pendingNames}. Kindly complete these items, validate the data, and proceed with closing the Retreat.`;
  }
  const barColor = doneCount === totalCount ? "#16a34a" : "#c2410c";
  const statusPills = checks
    .map((item) => {
      const tip = `${item.valid ? "Complete" : "Needs attention"}. ${item.message || ""}`;
      const background = item.valid ? "#ecfdf3" : "#fff7ed";
      const border = item.valid ? "#bbf7d0" : "#fed7aa";
      const color = item.valid ? "#166534" : "#9a3412";
      const pillLabel =
        item.key === "closed" ? "Retreat Validated and Closed" : item.label;
      return `<span title="${escapeHtml(tip)}" style="display:inline-block;margin:0 6px 6px 0;padding:4px 10px;border-radius:999px;border:1px solid ${border};background:${background};color:${color};font-size:12px;font-weight:700;line-height:1.2;text-decoration:none;">${escapeHtml(pillLabel)}</span>`;
    })
    .join("");

  return `
<p style="margin:0 0 14px;font-size:15px;line-height:1.6;color:#1e293b;">
  As you know, your Retreat, <span style="color:#1e293b;text-decoration:none;">${retreatName}</span> (Retreat ID: <span style="color:#1e293b;text-decoration:none;">${escapeHtml(
    row?.id,
  )}</span>)${
    completedOn ? `, was completed on ${escapeHtml(completedOn)}` : ""
  }.
</p>
<p style="margin:0 0 14px;font-size:15px;line-height:1.6;color:#1e293b;">
  The next step is to validate the Retreat data and formally close the Retreat.
</p>
<p style="margin:0 0 12px;font-size:15px;line-height:1.6;color:#1e293b;">
  Currently, <strong>${percent}%</strong> of the Retreat data has been uploaded and confirmed. ${escapeHtml(
    pendingSentence,
  )}
</p>
<table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin:0 0 12px;">
  <tr>
    <td style="font-size:13px;font-weight:700;color:#475569;white-space:nowrap;padding-right:10px;vertical-align:middle;">${doneCount} of ${totalCount} complete</td>
    <td style="vertical-align:middle;width:100%;">
      <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#eef2f6;border-radius:999px;">
        <tr>
          <td width="${percent}%" style="background:${barColor};height:8px;font-size:0;line-height:0;border-radius:999px;">&nbsp;</td>
          <td width="${100 - percent}%" style="height:8px;font-size:0;line-height:0;">&nbsp;</td>
        </tr>
      </table>
    </td>
    <td style="font-size:13px;font-weight:700;color:#9a3412;white-space:nowrap;padding-left:10px;vertical-align:middle;">${escapeHtml(remainingLabel)}</td>
  </tr>
</table>
<p style="margin:0 0 16px;line-height:1.8;">${statusPills}</p>
<p style="margin:0 0 14px;font-size:15px;line-height:1.6;color:#1e293b;">
  Open the Retreat Roster to finish the pending items:
  <a href="#RosterLink#" style="color:#8b1e3f;font-weight:700;text-decoration:underline;">#RosterLink#</a>
</p>
<p style="margin:0 0 14px;font-size:15px;line-height:1.6;color:#1e293b;">
  If you have any questions or need assistance, please contact us at
  <a href="mailto:${CLOSURE_CONTACT_EMAIL}" style="color:#8b1e3f;font-weight:700;text-decoration:underline;">${CLOSURE_CONTACT_EMAIL}</a>.
</p>
<p style="margin:0;font-size:15px;line-height:1.6;color:#1e293b;">Thank you for your cooperation.</p>`;
};

  const columns = [
    {
      id: "retreatId",
      name: "ID",
      selector: (row) => row?.id,
      cell: (row) => <span className="rc-id">{row?.id}</span>,
      sortable: true,
      reorder: true,
      wrap: false,
      width: "76px",
      grow: 0,
    },
    {
      id: "title",
      name: "Retreat",
      selector: (row) => formatRetreatTitle(row),
      cell: (row) => {
        const title = formatRetreatTitle(row) || "Untitled retreat";
        const shortTitle = shortenTitle(title);
        const displayDate =
          row.dateFrom && row.dateTo
            ? formatRetreatDates(row.dateFrom, row.dateTo)
            : null;
        const retreatants = Number(row.countRetreatant || 0);
        const team = Number(row.countTeam || 0);

        return (
          <div className="rc-retreat">
            <OverlayTrigger
              placement="top"
              delay={{ show: 180, hide: 80 }}
              popperConfig={{ strategy: "fixed" }}
              overlay={
                <Tooltip className="rc-tip">
                  <div className="rc-tip-body">{title}</div>
                </Tooltip>
              }
            >
              <a
                className="rc-retreat-link"
                href={`/retreatRoster?retreatId=${row.id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "block",
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {shortTitle}
              </a>
            </OverlayTrigger>
            {displayDate && <span className="rc-retreat-date">{displayDate}</span>}
            <span
              className="rc-retreat-meta"
              title="Verified retreatants and team on the roster"
            >
              {retreatants} retreatant{retreatants === 1 ? "" : "s"} · {team} team
            </span>
            {row.status === "INACTIVE" ? (
              <span className="rc-retreat-inactive">Inactive</span>
            ) : null}
          </div>
        );
      },
      sortable: true,
      reorder: true,
      wrap: false,
      width: "28%",
      grow: 1,
    },
    {
      id: "confirmations",
      name: "Status",
      wrap: true,
      width: "32%",
      grow: 1,
      cell: (row) => {
        const checks = getClosureChecks(row);
        const done = checks.filter((item) => item.valid).length;

        return (
          <div className="rc-checks">
            <div className="rc-checks-head">
              <span>
                {done} of {checks.length} complete
              </span>
              <span className="rc-bar" aria-hidden="true">
                <span
                  className={done === checks.length ? "is-full" : ""}
                  style={{ width: `${(done / checks.length) * 100}%` }}
                />
              </span>
            </div>
            <div className="rc-pills">
              {checks.map((item) => (
                <StatusPill
                  key={item.key}
                  label={item.label}
                  valid={item.valid}
                  message={item.message}
                />
              ))}
            </div>
          </div>
        );
      },
    },
    {
      id: "confirmed_by",
      name: "Confirmed By",
      wrap: false,
      width: "118px",
      minWidth: "118px",
      grow: 0,
      cell: (row) => (
        <div className="rc-confirmed-wrap">
          <OverlayTrigger
            placement="top"
            delay={{ show: 200, hide: 80 }}
            popperConfig={{ strategy: "fixed" }}
            overlay={
              <Tooltip className="rc-tip">
                <div className="rc-tip-body">Who confirmed this Retreat</div>
              </Tooltip>
            }
          >
            <button
              type="button"
              className="rc-confirmed-by mt-1"
              aria-label="Who confirmed this Retreat"
              onClick={() => {
                setRetreatDetail({
                  ...row,
                  displayTitle: formatRetreatTitle(row) || row.title,
                });
                setConfirmedData(true);
              }}
            >
              <i className="mdi mdi-view-headline" aria-hidden="true" />
            </button>
          </OverlayTrigger>
        </div>
      ),
    },
    {
      id: "Send Email",
      name: "Notify",
      wrap: true,
      width: showSuperAdminConfirm ? "320px" : "108px",
      minWidth: showSuperAdminConfirm ? "320px" : "108px",
      grow: 0,
      cell: (row) => {
        const retreatAlreadyClosed = isRetreatClosed(
          row.retreatCloseStatus,
          row.confirmedByAdmin,
        );
        return (
          <div className="rc-notify-actions">
            <OverlayTrigger
              placement="top"
              delay={{ show: 200, hide: 80 }}
              overlay={
                <Tooltip className="rc-tip">
                  <div className="rc-tip-body">
                    Email the lead couples and steering committee about this retreat.
                  </div>
                </Tooltip>
              }
            >
              <button
                type="button"
                className="rc-mail"
                onClick={() => {
                  setLeadMail(true);
                  setRetreatDetail(row);
                  setMailMessage(buildMailMessage(row));
                }}
              >
                <i className="mdi mdi-email-outline" aria-hidden="true" />
                Email
              </button>
            </OverlayTrigger>
            {showSuperAdminConfirm ? (
              <button
                type="button"
                className={`rc-super-confirm${
                  retreatAlreadyClosed ? " is-confirmed" : ""
                }`}
                disabled={retreatAlreadyClosed}
                onClick={() => {
                  if (retreatAlreadyClosed) return;
                  setSuperAdminConfirmRow(row);
                }}
              >
                {retreatAlreadyClosed
                  ? "Retreat Confirmed by Super-Admin"
                  : "Retreat Confirm by Super-Admin"}
              </button>
            ) : null}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    getRetreatClosures();
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchKey(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      getRetreatClosures(1, pageSize, value);
    }, 350);
  };

  const handleFilterChange = (filter) => {
    setDateFilter(filter);

    if (filter !== "custom") {
      setDateFrom(null);
      setDateTo(null);
    }

    applyDateFilter(filter, null, null);
  };

  const handleDateFromChange = (date) => {
    setDateFrom(date);
    if (date) {
      setDateFilter("custom");
      applyDateFilter("custom", date, dateTo);
    } else if (!dateTo) {
      setDateFilter("all");
      applyDateFilter("all", null, null);
    }
  };

  const handleDateToChange = (date) => {
    setDateTo(date);
    if (date) {
      setDateFilter("custom");
      applyDateFilter("custom", dateFrom, date);
    } else if (!dateFrom) {
      setDateFilter("all");
      applyDateFilter("all", null, null);
    }
  };

  const applyDateFilter = (filter, customDateFrom, customDateTo) => {
    const filterData = {
      filterType: filter,
      dateFrom: customDateFrom
        ? moment(customDateFrom).format("YYYY-MM-DD")
        : null,
      dateTo: customDateTo ? moment(customDateTo).format("YYYY-MM-DD") : null,
    };

    getRetreatClosures(
      1,
      pageSize,
      searchKey,
      {},
      false,
      null,
      "",
      null,
      filterData,
    );
  };

  const clearFilters = () => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    closeFilterRef.current = DEFAULT_CLOSE_FILTER;
    setCloseFilter(DEFAULT_CLOSE_FILTER);
    setDateFilter("all");
    setDateFrom(null);
    setDateTo(null);
    setSearchKey("");
    getRetreatClosures(1, pageSize, "", {}, false, null, "", null, {
      filterType: "all",
      dateFrom: null,
      dateTo: null,
    }, DEFAULT_CLOSE_FILTER);
  };

  const getRetreatClosures = async (
    pageNo = 1,
    size = 20,
    searchKeyword = null,
    sortBy = {},
    loadCount = false,
    coupleId,
    committeeId = "",
    parishValue = null,
    dateFilterData = null,
    statusFilter = null,
  ) => {
    const currentRequest = ++requestId.current;
    setLoadData(true);
    let d = {};

    if (pageNo && size) {
      d.pageNo = pageNo;
      d.size = size;
      if (Number(size) !== Number(pageSize)) setPageSize(Number(size));
    }

    if (searchKeyword !== null) {
      d.searchKeyword = searchKeyword;
    } else {
      d.searchKeyword = searchKey;
    }

    if (sortBy.sortColumn) {
      d.sortBy = sortBy;
    }

    d.export = false;
    d.coupleId = coupleId;
    d.committeeId = committeeId;
    d.parishValue = parishValue || null;
    d.dateFilter = dateFilterData || dateStateRef.current;
    d.closeFilter = statusFilter ?? closeFilterRef.current;

    if (userData?.committeeMember === true && userData?.userRole !== "ADMIN") {
      d.committeeId = userData.id;
    }

    await ClosureApi.getAllRetreatClosures(d)
      .then((res) => {
        if (currentRequest !== requestId.current) return;
        if (res.success) {
          let totalRecords = res.totalRecords ? res.totalRecords : 0;
          setTotal(totalRecords);
          if (res.summary) {
            setSummary({
              ended: Number(res.summary.ended || 0),
              closed: Number(res.summary.closed || 0),
              open: Number(res.summary.open || 0),
              inactive: Number(res.summary.inactive || 0),
            });
          }

          const rows = Array.isArray(res.data) ? res.data : [];
          const parsedData = rows.map((item) => {
            const { confirmation, schedule, ...rest } = item;
            const parsedSchedule = parseJsonField(schedule, []);
            const parsedConfirmation = parseJsonField(confirmation, []);
            return {
              ...rest,
              confirmation: parsedConfirmation,
              schedule: Array.isArray(parsedSchedule)
                ? parsedSchedule.map((entry) => ({
                    date: entry?.date ? new Date(entry.date) : null,
                    from: entry?.from,
                    to: entry?.to,
                  }))
                : [],
            };
          });

          setLoadData(false);

          if (parishValue && parishValue !== null) {
            const retreatIds = parsedData.map((retreat) => retreat.id);
            setSelectedRetreats(retreatIds);
          }

          setAllRetreatClosures(parsedData);
        } else {
          setLoadData(false);
          toast.error(res.msg);
        }
      })
      .catch((error) => {
        if (currentRequest !== requestId.current) return;
        setLoadData(false);
        toast.error(error.message || "An error occurred");
      });
  };

  const getActiveFilterLabel = () => {
    if (dateFilter === "current")
      return `This month (${moment().format("MMMM YYYY")})`;
    if (dateFilter === "previous")
      return `Last month (${moment()
        .subtract(1, "month")
        .format("MMMM YYYY")})`;
    if (dateFilter === "custom" && (dateFrom || dateTo)) {
      const from = dateFrom ? moment(dateFrom).format("MMM D, YYYY") : "Start";
      const to = dateTo ? moment(dateTo).format("MMM D, YYYY") : "End";
      return `${from} - ${to}`;
    }
    return "";
  };

  const handleStatusFilter = (next) => {
    if (next === closeFilter) return;
    closeFilterRef.current = next;
    setCloseFilter(next);
    getRetreatClosures(
      1,
      pageSize,
      searchKey,
      {},
      false,
      null,
      "",
      null,
      dateStateRef.current,
      next,
    );
  };

  const statusTitle =
    CLOSE_FILTER_TITLES[closeFilter] || CLOSE_FILTER_TITLES.open;
  const dateFilterLabel = getActiveFilterLabel();
  const hasClearableFilters =
    dateFilter !== "all" ||
    Boolean(searchKey) ||
    Boolean(dateFrom) ||
    Boolean(dateTo) ||
    closeFilter !== DEFAULT_CLOSE_FILTER;

  const sendMailToLead = async (payload = {}) => {
    const messageContent = payload.message || "";
    if (
      !messageContent ||
      messageContent
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, "")
        .trim() === ""
    ) {
      toast.error("Please enter a message");
      return;
    }

    await ClosureApi.sendMailToLead({
      retreatId: retreatDetail.id,
      subject: payload.subject,
      message: messageContent,
      recipients: payload.recipients || [],
      additionalEmails: payload.additionalEmails || [],
      autoRemind: Boolean(payload.autoRemind),
    })
      .then((res) => {
        if (res.success) {
          handleCloseMail();
          toast.success(res.msg);
        } else {
          toast.error(res.msg);
        }
      })
      .catch((error) => {
        toast.error(error.message || "An error occurred");
      });
  };

  const handleCloseMail = () => {
    setLeadMail(false);
    setRetreatDetail([]);
    setMailMessage("Test Content");
  };

  const confirmRetreatAsSuperAdmin = () => {
    const row = superAdminConfirmRow;
    if (!row?.id) return;
    retreatApi
      .retreatCloseMailSent({
        retreatId: row.id,
        userData: {
          firstName: loggedInUser?.firstName,
          lastName: loggedInUser?.lastName,
          userName: loggedInUser?.userName,
          email: loggedInUser?.email,
          userRole: loggedInUser?.userRole,
        },
      })
      .then((res) => {
        if (res.success) {
          toast.success(res.message);
          setSuperAdminConfirmRow(null);
          getRetreatClosures();
        } else {
          toast.error(res.message);
        }
      })
      .catch((error) => {
        toast.error(error?.message || "Failed to update retreat status.");
      });
  };

  const fetchRecipients = async (retreatId) => {
    const res = await ClosureApi.getMailRecipients({ retreatId: retreatId });
    if (!res.success) throw new Error(res.msg);
    return res.data;
  };

  return (
    <div className={`rc-page${showSuperAdminConfirm ? " rc-can-confirm" : ""}`}>
      <header className="rc-header">
        <div>
          <p className="rc-kicker">Post-retreat</p>
          <div className="rc-title-row">
            <h1 className="rc-title">Retreat Closures</h1>
            <span className="rc-count">{total || 0}</span>
          </div>
        </div>
        <div className="rc-guide-cards">
          <ClosureGuideCard
            guide={rosterGuide}
            onOpen={() => training?.openGuide("what-is-roster")}
          />
          <ClosureGuideCard
            guide={directoryGuide}
            onOpen={() => training?.openGuide("what-is-directory")}
          />
          <ClosureGuideCard
            guide={picturesGuide}
            onOpen={() => training?.openGuide("what-is-pictures")}
          />
        </div>
      </header>

      <section className="rc-stats" aria-label="Retreat status">
        <p className="rc-stats-caption">Click a status to switch the list</p>
        <div className="rc-stats-toggle" role="radiogroup" aria-label="Retreat status">
          <button
            type="button"
            role="radio"
            className={`rc-stat rc-stat-ok ${closeFilter === "closed" ? "is-active" : ""}`}
            onClick={() => handleStatusFilter("closed")}
            aria-checked={closeFilter === "closed"}
            title="Show retreats that are validated and closed"
          >
            <span className="rc-stat-switch" aria-hidden="true" />
            <i className="mdi mdi-check-circle-outline" aria-hidden="true" />
            <span className="rc-stat-copy">
              <span className="rc-stat-label">Retreats Validated &amp; Closed</span>
              <span className="rc-stat-action">
                {closeFilter === "closed" ? "Showing" : "Click to view"}
              </span>
            </span>
            <span className="rc-stat-value is-ok">{summary.closed}</span>
          </button>
          <button
            type="button"
            role="radio"
            className={`rc-stat rc-stat-open ${closeFilter === "open" ? "is-active" : ""}`}
            onClick={() => handleStatusFilter("open")}
            aria-checked={closeFilter === "open"}
            title="Show retreats that are not closed"
          >
            <span className="rc-stat-switch" aria-hidden="true" />
            <i className="mdi mdi-clock-outline" aria-hidden="true" />
            <span className="rc-stat-copy">
              <span className="rc-stat-label">Retreats Not Closed</span>
              <span className="rc-stat-action">
                {closeFilter === "open" ? "Showing" : "Click to view"}
              </span>
            </span>
            <span className="rc-stat-value is-open">{summary.open}</span>
          </button>
          <button
            type="button"
            role="radio"
            className={`rc-stat rc-stat-inactive ${closeFilter === "inactive" ? "is-active" : ""}`}
            onClick={() => handleStatusFilter("inactive")}
            aria-checked={closeFilter === "inactive"}
            title="Show inactive retreats"
          >
            <span className="rc-stat-switch" aria-hidden="true" />
            <i className="mdi mdi-minus-circle-outline" aria-hidden="true" />
            <span className="rc-stat-copy">
              <span className="rc-stat-label">Inactive Retreats</span>
              <span className="rc-stat-action">
                {closeFilter === "inactive" ? "Showing" : "Click to view"}
              </span>
            </span>
            <span className="rc-stat-value is-inactive">{summary.inactive}</span>
          </button>
        </div>
      </section>

      <div className="rc-toolbar">
        <div className="rc-filters" role="group" aria-label="Date range">
          <button
            type="button"
            className={`rc-filter ${dateFilter === "all" ? "is-active" : ""}`}
            onClick={() => handleFilterChange("all")}
          >
            All Retreats
          </button>
          <button
            type="button"
            className={`rc-filter ${dateFilter === "current" ? "is-active" : ""}`}
            onClick={() => handleFilterChange("current")}
          >
            This Month
          </button>
          <button
            type="button"
            className={`rc-filter ${dateFilter === "previous" ? "is-active" : ""}`}
            onClick={() => handleFilterChange("previous")}
          >
            Last Month
          </button>
        </div>

        <div className={`rc-range ${dateFilter === "custom" ? "is-active" : ""}`}>
          <label className="rc-range-field">
            <span>From</span>
            <span className="rc-range-control">
              <i className="mdi mdi-calendar-blank-outline" aria-hidden="true" />
              <DatePicker
                selected={dateFrom}
                onChange={handleDateFromChange}
                dateFormat="MM-dd-yyyy"
                placeholderText="mm-dd-yyyy"
                className="rc-range-input"
                maxDate={dateTo || new Date()}
                isClearable
                showDisabledMonthNavigation
                autoComplete="off"
              />
            </span>
          </label>
          <span className="rc-range-arrow" aria-hidden="true">
            <i className="mdi mdi-arrow-right" />
          </span>
          <label className="rc-range-field">
            <span>To</span>
            <span className="rc-range-control">
              <i className="mdi mdi-calendar-blank-outline" aria-hidden="true" />
              <DatePicker
                selected={dateTo}
                onChange={handleDateToChange}
                dateFormat="MM-dd-yyyy"
                placeholderText="mm-dd-yyyy"
                className="rc-range-input"
                minDate={dateFrom}
                maxDate={new Date()}
                isClearable
                showDisabledMonthNavigation
                autoComplete="off"
              />
            </span>
          </label>
        </div>

        <label className="rc-search">
          <i className="mdi mdi-magnify" aria-hidden="true" />
          <input
            onChange={handleSearch}
            type="search"
            placeholder="Search name, parish, or ID"
            aria-label="Search retreats"
            value={searchKey}
          />
        </label>

        {hasClearableFilters && (
          <button type="button" className="rc-clear" onClick={clearFilters}>
            Clear
          </button>
        )}
      </div>

      <div className="rc-active-filter">
        <div className="rc-active-filter-side">
          {dateFilter !== "all" && dateFilterLabel ? (
            <span className="rc-chip">{dateFilterLabel}</span>
          ) : null}
        </div>
        <h2 className="rc-view-title">{statusTitle}</h2>
        <div className="rc-active-filter-side is-end">
          {searchKey ? <span className="rc-chip">Search: {searchKey}</span> : null}
        </div>
      </div>

      <section className="rc-table-card" aria-label="Retreat closure list">
        <CustomDataTable
          key={`${closeFilter}-${dateFilter}-${dateFrom ? dateFrom.getTime() : ""}-${dateTo ? dateTo.getTime() : ""}`}
          loading={LoadData}
          data={allRetreatClosures}
          countPerPage={pageSize}
          isServerSide={true}
          pagination={true}
          sortServer={true}
          total={total}
          columns={columns}
          dataFetchApi={getRetreatClosures}
          pointerOnHover={false}
          highlightOnHover={true}
          spinnerColor="#8b1e3f"
          committeeId={showingRetreats ? userData?.id : null}
          parishValue={locationData?.state?.parishValue}
          conditionalRowStyles={[
            {
              when: (row) => row.status === "INACTIVE",
              style: { boxShadow: "inset 3px 0 0 #64748b" },
            },
            {
              when: (row) =>
                row.status !== "INACTIVE" &&
                isRetreatClosed(row.retreatCloseStatus, row.confirmedByAdmin),
              style: { boxShadow: "inset 3px 0 0 #16a34a" },
            },
          ]}
          noDataComponent={
            <div className="rc-empty">
              <h3>No retreats found</h3>
              <p>Try another date range, or clear the search.</p>
              {hasClearableFilters && (
                <button type="button" className="rc-clear" onClick={clearFilters}>
                  Clear filters
                </button>
              )}
            </div>
          }
        />
      </section>

      {confirmedData ? (
        <ConfirmationAuditModal
          show={confirmedData}
          retreat={retreatDetail}
          onClose={() => setConfirmedData(false)}
        />
      ) : null}

      {leadMail && (
        <MailRecipientsModal
          show={leadMail}
          handleClose={handleCloseMail}
          retreatDetail={retreatDetail}
          fetchRecipients={fetchRecipients}
          initialMessage={mailMessage}
          actionFunction={(payload) => {
            setMailMessage(payload.message);
            sendMailToLead(payload);
          }}
          cancel="Cancel"
          okay="Send"
        />
      )}
      {superAdminConfirmRow ? (
        <AlertModal
          appearance="confirm"
          show={Boolean(superAdminConfirmRow)}
          handleClose={() => setSuperAdminConfirmRow(null)}
          message={`Are you sure you want to confirm "${formatRetreatTitle(
            superAdminConfirmRow,
          )}" Retreat? Confirming marks Roster Data, Directory, Program, and Pictures as confirmed and closes this Retreat, even when older data is incomplete.`}
          actionFunction={confirmRetreatAsSuperAdmin}
          cancel={"Cancel"}
          okay={"Confirm retreat"}
          heading={"Confirm retreat"}
        />
      ) : null}
    </div>
  );
};

export default ClosuresList;
